import React from "react";
import { useSelector } from "react-redux";
import { ClipboardList, HeartPulse, History, PanelLeft, Sparkles, X } from "lucide-react";
import "./chartframe.css";
import { chartTabAllowed, pulseChatShown } from "../../../config/practice";

// Chart top bar: sidebar toggle, the chart tabs (CareIQ first), and the
// Pulse / Orders / Timeline / close actions on the right.
export const NavFrame = ({
  onTabClick,
  hasRpmAccess,
  activeTab,
  onToggleSidebar,
  sidebarCollapsed,
  showTimeline,
  onToggleTimeline,
  onOrders,
  // the Orders button only below 1024 px (the N3 chart window: from 1024 px
  // its vertical Orders tab on the right edge is the toggle)
  ordersBelowLgOnly = false,
  onClose,
  // this chart window's CareIQ flag is "n3" (patientchartwrap): the CareIQ tab
  // of a practice where it is N3-only (7825) shows only then
  careiqN3 = false,
  // Pulse chart assistant (patientchartwrap renders the panel while open).
  // The button shows where config/practice pulseChatShown() allows it.
  pulseOpen = false,
  setPulseOpen,
}) => {
  const homescreen = useSelector(state => state.userPrivileges.homescreen);
  // Tabs keep their ids (renderContent in patientchartwrap switches on them).
  // Summary (id 0) is intentionally not listed - CareIQ replaces it as the
  // chart's landing view; case 0 stays in patientchartwrap so anything that
  // still routes to it keeps rendering.
  const allTabs = [
    { id: 11, label: "CareIQ", icon: <Sparkles /> },
    { id: 10, label: "Non-visit notes", requiresHomescreen: "Threads" },
    { id: 1, label: "Labs" },
    { id: 2, label: "Documents" },
    { id: 3, label: "Vitals" },
    { id: 4, label: "RPM", requires: true },
    { id: 5, label: "Prior visits" },
    // { id: 6, label: "Imaging" },
    // Re-enabled 2026-09-02: the questionnaire fill flow (bangquestions
    // migration) lives here alongside the completed-forms history.
    { id: 7, label: "Forms" },
    { id: 8, label: "CCM" },
    { id: 9, label: "Medications" },
  ];

  const tabs = allTabs.filter((tab) => {
    // The practice's chart tab allowlist (7825: Vitals, Forms, and CareIQ for
    // an "n3" chart; 9928: all).
    if (!chartTabAllowed(tab.id, careiqN3)) return false;
    // RPM condition
    if (tab.requires && !hasRpmAccess) return false;
    // Homescreen privilege condition
    if (tab.requiresHomescreen && !homescreen.includes(tab.requiresHomescreen)) return false;
    return true;
  });

  const showPulse = typeof setPulseOpen === "function" && pulseChatShown(careiqN3);

  return (
    <div className="bmch bmch-bar">
      {onToggleSidebar && (
        <button
          type="button"
          className="bmch-collapse"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? "Show patient panel" : "Hide patient panel"}
          aria-label={sidebarCollapsed ? "Show patient panel" : "Hide patient panel"}
        >
          <PanelLeft />
        </button>
      )}
      <div className="bmch-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            key={tab.id}
            className={`bmch-tab${activeTab === tab.id ? " on" : ""}`}
            onClick={() => onTabClick(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
      <div className="bmch-bar-actions">
        {showPulse && (
          <button
            type="button"
            className={`bmch-bar-btn bmch-bar-pulse${pulseOpen ? " on" : ""}`}
            onClick={() => setPulseOpen((v) => !v)}
            aria-expanded={pulseOpen}
            aria-controls="pulse-panel"
            aria-label="Pulse chart assistant"
            title="Pulse — Chart Assistant"
            data-act="chartPulse"
          >
            <HeartPulse aria-hidden="true" />
            <span className="bmch-bar-pulse-label">Pulse</span>
          </button>
        )}
        {onOrders && (
          <button type="button" className={`bmch-bar-btn${ordersBelowLgOnly ? " bmch-bar-orders-sm" : ""}`} onClick={onOrders} data-act="chartOrders">
            <ClipboardList />
            Orders
          </button>
        )}
        {onToggleTimeline && activeTab === 11 && (
          <button
            type="button"
            className={`bmch-bar-btn${showTimeline ? " on" : ""}`}
            onClick={onToggleTimeline}
            aria-pressed={showTimeline}
          >
            <History />
            Timeline
          </button>
        )}
        {onClose && (
          <button type="button" className="bmch-bar-btn icon" onClick={onClose} title="Close chart" aria-label="Close chart">
            <X />
          </button>
        )}
      </div>
    </div>
  );
};
